import { ReactQuery } from 'cms-api'
import Link from 'next/link'
import React from 'react'
import { ROUTE } from 'shared-constants'
import { Button, BUTTON_VARIANT, BUTTON_ICON_POSITION } from 'components/Button'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import Icon from 'components/Icon/Icon'
import { Box, Flex, Grid } from 'components/Layout'
import { Text } from 'components/Typography'
import { getSelectedVariant } from 'components/UserFavorites/UserRecentlyViewedProducts'
import { WrappedLoader } from 'components/WrappedLoader'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_NAMES, FONT_SIZE, FONT_WEIGHT } from 'Theme'
import { getProductRoute } from 'utils/getProductRoute'
import { getProductSlug, getVariantSlug } from 'utils/getProductSlug'

const GREEN_GRADIENT = 'linear-gradient(135deg, #EBF2F1 0%, #C8D8D5 100%)'
const INK = COLOR_NAMES.EEIRE_BLACK
const MUTED = COLOR_NAMES.GRAY_SHINGLE
const TILE_SIZE = 74
const MAX_TILES = 4

type PaginatedMeta = { meta?: { pagination?: { total?: number } } }

const getTotal = (response: unknown, fallback: number) =>
  (response as PaginatedMeta)?.meta?.pagination?.total ?? fallback

const relativeTime = (dateStr?: string) => {
  if (!dateStr) {
    return ''
  }
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000)
  if (days <= 0) {
    return 'today'
  }
  if (days === 1) {
    return 'yesterday'
  }
  if (days < 7) {
    return `${days} days ago`
  }
  const weeks = Math.floor(days / 7)
  if (weeks < 5) {
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`
  }
  const months = Math.floor(days / 30)
  return `${months} month${months > 1 ? 's' : ''} ago`
}

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <Text
    color={MUTED}
    fontSize={FONT_SIZE.PX_11}
    fontWeight={FONT_WEIGHT.SEMI_BOLD}
    letterSpacing="0.14em"
    textTransform="uppercase"
    as="p"
  >
    {children}
  </Text>
)

export const ExpertFavoritesWidget = () => {
  const { user } = useUserAuth()

  const { data: recentlyViewedData, isLoading: isRecentlyViewedLoading } =
    ReactQuery.useGetRecentlyViewedUserProductHistory(
      {
        populate: {
          productVariants: {
            populate: {
              productVariantInventory: true,
              images: true,
              leadProductImage: true,
            },
          },
        } as never,
        'pagination[pageSize]': 8,
        sort: 'updatedAt:asc',
      },
      { query: { queryKey: ['recentlyViewed', user?.id] } }
    )

  const { data: favoritesData, isLoading: isFavoritesLoading } =
    ReactQuery.useGetFavoriteUserProductVariantBookmark(
      {
        populate: {
          productVariant: { populate: { product: true } },
        } as never,
        'pagination[pageSize]': 1,
        sort: 'updatedAt:desc',
        filters: { isBookmarked: true },
      },
      { query: { queryKey: ['favorites', user?.id] } }
    )

  const recentlyViewed = (recentlyViewedData?.data ?? [])
    .map((item) => {
      const mainVariant = getSelectedVariant(item.productVariants)
      const imageUrl =
        mainVariant?.leadProductImage?.url || mainVariant?.images?.[0]?.url
      if (!mainVariant || !imageUrl) {
        return null
      }
      return {
        id: item.id,
        title: item.title,
        imageUrl,
        href: getProductRoute({
          slug: getProductSlug({ id: item.legacyId, title: item.title }),
          variantSlug: getVariantSlug({
            id: mainVariant.legacyId,
            title: item.title,
          }),
        }),
      }
    })
    .filter(Boolean)

  const recentlyViewedTotal = getTotal(
    recentlyViewedData,
    recentlyViewed.length
  )
  const favoritesTotal = getTotal(
    favoritesData,
    favoritesData?.data?.length ?? 0
  )
  const lastFavorite = favoritesData?.data?.[0]
  const hasRecentlyViewed = recentlyViewed.length > 0
  const hasFavorites = favoritesTotal > 0

  const tiles = recentlyViewed.slice(0, MAX_TILES)
  const remaining = recentlyViewedTotal - tiles.length

  return (
    <Card
      flexDirection="column"
      width="100%"
      elevation={CARD_ELEVATION.NONE}
      hasBorders
      paddingSize={CARD_PADDING_SIZE.NONE}
      overflow="hidden"
    >
      {isRecentlyViewedLoading || isFavoritesLoading ? (
        <Flex justifyContent="center" alignItems="center" minHeight="220px">
          <WrappedLoader />
        </Flex>
      ) : (
        <Grid
          gridTemplateColumns={{ MOBILE: '1fr', TABLET: '0.9fr 1.1fr' }}
          width="100%"
        >
          {/* LEFT — Recently Viewed (gold) */}
          <Flex
            position="relative"
            p="26px 30px"
            minHeight="220px"
            alignItems="flex-end"
            style={{ background: GREEN_GRADIENT }}
          >
            <Flex
              position="absolute"
              alignItems="center"
              gap="6px"
              backgroundColor="white"
              px="8px"
              py="4px"
              style={{ top: 16, left: 16, borderRadius: 99 }}
            >
              <Icon.EyeOpen size={12} color={INK} />
              <Text
                fontSize={FONT_SIZE.PX_11}
                fontWeight={FONT_WEIGHT.SEMI_BOLD}
                letterSpacing="0.1em"
                textTransform="uppercase"
                color={MUTED}
                as="span"
              >
                Recently Viewed
              </Text>
            </Flex>

            {hasRecentlyViewed ? (
              <Flex style={{ marginLeft: 4 }}>
                {tiles.map((item, i) => (
                  <Link key={item.id} href={item.href} passHref>
                    <Box
                      width={`${TILE_SIZE}px`}
                      height={`${TILE_SIZE}px`}
                      style={{
                        borderRadius: 12,
                        border: '2px solid #FFFFFF',
                        marginLeft: i === 0 ? 0 : -16,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                        backgroundImage: `url(${item.imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        transform: `rotate(${(i - 1.5) * 3}deg)`,
                      }}
                    />
                  </Link>
                ))}
                {remaining > 0 && (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    width={`${TILE_SIZE}px`}
                    height={`${TILE_SIZE}px`}
                    color="white"
                    style={{
                      borderRadius: 12,
                      border: '2px solid #FFFFFF',
                      marginLeft: -16,
                      background: INK,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      transform: 'rotate(6deg)',
                    }}
                  >
                    <Text color="white" fontSize={FONT_SIZE.PX_18} as="span">
                      +{remaining}
                    </Text>
                  </Flex>
                )}
              </Flex>
            ) : (
              <Text color={MUTED} fontSize={FONT_SIZE.PX_13} as="p">
                Nothing viewed yet — start exploring products.
              </Text>
            )}
          </Flex>

          {/* RIGHT — Favorites */}
          <Flex
            flexDirection="column"
            justifyContent="center"
            gap="14px"
            p="26px 32px"
            backgroundColor="white"
          >
            <Eyebrow>Pick up where you left off</Eyebrow>
            <Box>
              <Text
                color={INK}
                fontSize={FONT_SIZE.PX_29}
                fontWeight={FONT_WEIGHT.MEDIUM}
                as="p"
              >
                {favoritesTotal}
              </Text>
              <Eyebrow>Saved items</Eyebrow>
            </Box>
            {hasFavorites && lastFavorite ? (
              <Text color={MUTED} fontSize={FONT_SIZE.PX_13} as="p">
                Last added {relativeTime(lastFavorite.updatedAt)}
                {lastFavorite.productVariant?.product?.title
                  ? ` · ${lastFavorite.productVariant.product.title}`
                  : ''}
              </Text>
            ) : (
              <Text color={MUTED} fontSize={FONT_SIZE.PX_13} as="p">
                Save products you love to find them here.
              </Text>
            )}
            <Box style={{ alignSelf: 'flex-start' }}>
              <Link
                href={
                  hasFavorites
                    ? ROUTE.EXPERT_FAVORITES()
                    : ROUTE.SHOWROOM_PRODUCTS()
                }
              >
                <Button
                  variant={BUTTON_VARIANT.FILLED}
                  width="auto"
                  icon={Icon.ArrowRight}
                  iconPosition={BUTTON_ICON_POSITION.RIGHT}
                >
                  {hasFavorites ? 'Open Favorites' : 'Start Favoriting'}
                </Button>
              </Link>
            </Box>
          </Flex>
        </Grid>
      )}
    </Card>
  )
}
